import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Zap, BadgeIndianRupee, FileLock2, FileText, Sparkles, Wallet, Briefcase, LineChart, ArrowRight, Star, CheckCircle2, Users, Calculator, ScrollText, ClipboardCheck, BadgeCheck, HandCoins } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { LeadForm } from "../components/LeadForm";
import { FAQSection } from "../components/FAQSection";
import { EMICalculator } from "../components/EMICalculator";

const TRUST = [
  { icon: ShieldCheck, label: "RBI Registered Partners" },
  { icon: Zap, label: "Fast Approval Process" },
  { icon: BadgeIndianRupee, label: "Multiple Bank Offers" },
  { icon: FileLock2, label: "256-bit Secure Data" },
  { icon: FileText, label: "100% Paperless Application" },
];

const PRODUCTS = [
  {
    icon: Wallet, title: "Personal Loan", to: "/personal-loan",
    desc: "Instant cash for weddings, travel, medical or any personal need.",
    bullets: ["Loans up to ₹50 Lakh", "Interest from 9.99% p.a.", "Tenure up to 7 years", "Minimal documentation"]
  },
  {
    icon: LineChart, title: "Flexi Overdraft Loan", to: "/overdraft-loan",
    desc: "Withdraw anytime, pay interest only on what you use.",
    bullets: ["Limits up to ₹2 Crore", "Pay interest on usage", "Flexible repayment", "Reusable credit line"]
  },
  {
    icon: Briefcase, title: "Business Loan", to: "/business-loan",
    desc: "Scale operations, fund inventory, expand to new markets.",
    bullets: ["Loans up to ₹2 Crore", "Collateral-free options", "Tenure up to 5 years", "Fast turnaround"]
  },
];

const WHY = [
  { icon: Zap, title: "Fast Processing", desc: "Get matched & approved in as little as 24 hours." },
  { icon: Users, title: "Multiple Lender Options", desc: "Compare 16+ Banks & NBFCs in one place." },
  { icon: BadgeIndianRupee, title: "Competitive Interest Rates", desc: "Negotiated rates not available individually." },
  { icon: Sparkles, title: "Expert Assistance", desc: "Dedicated advisors guide you end-to-end." },
  { icon: ClipboardCheck, title: "End-to-End Support", desc: "From eligibility to disbursal, we're with you." },
  { icon: ShieldCheck, title: "Secure Application", desc: "Bank-grade encryption protects every detail." },
];

const STEPS = [
  { n: "01", title: "Check Eligibility", desc: "Tell us your income, city and need. No CIBIL hit." },
  { n: "02", title: "Compare Offers", desc: "See pre-approved offers from 16+ lenders side-by-side." },
  { n: "03", title: "Submit Application", desc: "Upload KYC & income docs — fully digital." },
  { n: "04", title: "Verification", desc: "Lender verifies. Our advisor tracks every step." },
  { n: "05", title: "Loan Disbursal", desc: "Funds credited directly to your bank account." },
];

const CALCULATORS = [
  { icon: Calculator, label: "EMI Calculator", to: "/tools/emi-calculator" },
  { icon: BadgeCheck, label: "Eligibility Calculator", to: "/tools/eligibility-calculator" },
  { icon: HandCoins, label: "Balance Transfer", to: "/tools/balance-transfer-calculator" },
  { icon: ScrollText, label: "Part Payment", to: "/tools/part-payment-calculator" },
  { icon: LineChart, label: "SIP Calculator", to: "/tools/sip-calculator" },
  { icon: BadgeIndianRupee, label: "Income Tax Calculator", to: "/tools/income-tax-calculator" },
];

const REVIEWS = [
  { name: "Rohit Sharma", city: "Mumbai", rating: 5, text: "Got my personal loan approved in 18 hours. The team compared 6 offers and saved me 2.1% on interest. Genuine service.", initials: "RS" },
  { name: "Priya Iyer", city: "Bengaluru", rating: 5, text: "As a self-employed consultant, I was rejected by 2 banks. OneLoan matched me with the right NBFC and I got ₹15L sanctioned.", initials: "PI" },
  { name: "Aman Verma", city: "Delhi NCR", rating: 5, text: "Business OD limit of ₹40 Lakh, set up entirely online. Withdraw whenever I need, pay interest only on usage. Game changer.", initials: "AV" },
];

const Stars = () => (
  <div className="flex">{[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} fill="#F59E0B" stroke="#F59E0B" />)}</div>
);

const SectionHeading = ({ eyebrow, title, sub, center }) => (
  <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
    <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">{eyebrow}</p>
    <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#273B59] font-heading">{title}</h2>
    {sub && <p className="mt-3 text-[#475569]">{sub}</p>}
  </div>
);

const Hero = () => (
  <section className="relative ol-grain bg-white">
    <div className="ol-container py-12 lg:py-20">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0 mb-5 px-3 py-1.5">
            <Sparkles size={12} className="mr-1.5" /> India's trusted loan marketplace
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-[#273B59] tracking-tight leading-[1.05]">
            Get the <span className="ol-gradient-text">right loan</span><br />faster.
          </h1>
          <p className="mt-5 text-lg text-[#475569] max-w-xl leading-relaxed">
            Compare and apply for Personal Loans, Business Loans, and Flexi Overdraft Loans from India's trusted lending partners — all in one place.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/apply"><Button data-testid="hero-apply-btn" size="lg" className="ol-btn-primary h-12 px-7 text-base">Apply Now <ArrowRight size={16} className="ml-1.5" /></Button></Link>
            <Link to="/tools/compare-loans"><Button data-testid="hero-compare-btn" size="lg" variant="outline" className="h-12 px-7 text-base border-[#273B59] text-[#273B59] hover:bg-[#F7F9FC]">Compare Loans</Button></Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#475569]">
            <div className="flex items-center gap-1.5"><Stars /><span className="font-semibold text-[#273B59]">4.8/5</span> · 12,000+ customers</div>
            <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#16A34A]" /> 256-bit secure</div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <LeadForm title="Check Eligibility Free" source="hero" />
        </div>
      </div>
    </div>
  </section>
);

const TrustBar = () => (
  <section data-testid="trust-bar" className="bg-[#273B59] text-white">
    <div className="ol-container py-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
        {TRUST.map((t) => (
          <div key={t.label} className="flex items-center gap-3">
            <t.icon size={22} className="text-[#1088D4]" />
            <span className="text-sm font-medium">{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProductsSection = () => (
  <section data-testid="products-section" className="ol-section">
    <div className="ol-container">
      <SectionHeading eyebrow="Our Products" title="Loan products for every need" sub="Pick from three flagship loan categories. Apply once — match across 16+ lenders." />
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {PRODUCTS.map((p, i) => (
          <div key={p.title} data-testid={`product-card-${i}`} className="ol-card-hover bg-white border border-[#E2E8F0] rounded-2xl p-7">
            <div className="w-12 h-12 rounded-lg bg-[#EAF5FD] flex items-center justify-center">
              <p.icon className="text-[#1088D4]" size={22} />
            </div>
            <h3 className="mt-5 text-xl font-bold text-[#273B59] font-heading">{p.title}</h3>
            <p className="mt-2 text-sm text-[#475569]">{p.desc}</p>
            <ul className="mt-5 space-y-2">
              {p.bullets.map(b => <li key={b} className="flex items-start gap-2 text-sm text-[#273B59]"><CheckCircle2 size={14} className="text-[#16A34A] mt-0.5 shrink-0" />{b}</li>)}
            </ul>
            <Link to={p.to} className="mt-6 inline-flex items-center text-sm font-semibold text-[#1088D4] hover:gap-2 transition-all gap-1">View details <ArrowRight size={14} /></Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhySection = () => (
  <section data-testid="why-section" className="bg-[#F7F9FC] ol-section">
    <div className="ol-container">
      <SectionHeading center eyebrow="Why OneLoan" title="Built for trust. Tuned for speed." />
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {WHY.map((w) => (
          <div key={w.title} className="bg-white p-6 rounded-xl border border-[#E2E8F0] ol-card-hover">
            <w.icon className="text-[#1088D4]" size={22} />
            <h3 className="mt-4 font-bold text-[#273B59] font-heading">{w.title}</h3>
            <p className="mt-2 text-sm text-[#475569]">{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowSection = () => (
  <section data-testid="how-section" className="ol-section">
    <div className="ol-container">
      <SectionHeading center eyebrow="How it works" title="From application to disbursal in 5 steps" />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {STEPS.map((s) => (
          <div key={s.n} className="relative">
            <div className="text-5xl font-bold ol-gradient-text font-heading">{s.n}</div>
            <h3 className="mt-3 font-bold text-[#273B59] font-heading">{s.title}</h3>
            <p className="mt-2 text-sm text-[#475569]">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CalcHub = () => (
  <section data-testid="calc-hub" className="bg-gradient-to-br from-[#EAF5FD] to-white ol-section">
    <div className="ol-container">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#1088D4] font-semibold">Tools</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#273B59] font-heading">Calculators that help you decide</h2>
        </div>
        <Link to="/tools/emi-calculator" className="text-sm font-semibold text-[#1088D4] hover:underline">View all tools →</Link>
      </div>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CALCULATORS.map((c) => (
          <Link key={c.label} to={c.to} className="bg-white p-5 rounded-xl border border-[#E2E8F0] hover:border-[#1088D4] transition-colors text-center group">
            <c.icon size={22} className="mx-auto text-[#1088D4]" />
            <div className="mt-2 text-xs font-semibold text-[#273B59]">{c.label}</div>
          </Link>
        ))}
      </div>
      <div className="mt-10">
        <EMICalculator productLabel="Loan" />
      </div>
    </div>
  </section>
);

const ReviewsSection = () => (
  <section data-testid="reviews-section" className="ol-section">
    <div className="ol-container">
      <SectionHeading center eyebrow="Customers" title="12,000+ Indians trust OneLoan" />
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {REVIEWS.map((r) => (
          <div key={r.name} className="bg-white p-6 rounded-xl border border-[#E2E8F0] ol-card-hover">
            <Stars />
            <p className="mt-4 text-sm text-[#273B59] leading-relaxed">"{r.text}"</p>
            <div className="mt-5 flex items-center gap-3 pt-4 border-t border-[#E2E8F0]">
              <div className="w-10 h-10 rounded-full bg-[#1088D4] text-white flex items-center justify-center font-semibold text-sm">{r.initials}</div>
              <div>
                <div className="text-sm font-semibold text-[#273B59]">{r.name}</div>
                <div className="text-xs text-[#475569]">{r.city}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta" className="bg-[#273B59] text-white ol-section relative overflow-hidden">
    <div className="absolute inset-0 ol-dots opacity-30"></div>
    <div className="ol-container relative text-center">
      <h2 className="text-3xl sm:text-5xl font-bold font-heading">Get your loan approved today.</h2>
      <p className="mt-4 text-white/80 max-w-xl mx-auto">Free, fast, and absolutely confidential. Pre-approved offers in under 5 minutes.</p>
      <div className="mt-7 flex flex-wrap gap-3 justify-center">
        <Link to="/apply"><Button data-testid="final-apply-btn" size="lg" className="ol-btn-primary h-12 px-8">Apply Now <ArrowRight size={16} className="ml-1.5" /></Button></Link>
        <a href="tel:+919370251358"><Button data-testid="final-call-btn" size="lg" variant="outline" className="h-12 px-8 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white">Talk to Advisor</Button></a>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBar />
      <ProductsSection />
      <WhySection />
      <HowSection />
      <CalcHub />
      <ReviewsSection />
      <FAQSection />
      <FinalCta />
    </div>
  );
}
